import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { numberToCurrencyStringValue, sanitizeCurrencyStringValue } from '../../../../../models/UiUtils';
import { useInfiniteBoardsQuery } from '../../../../../queries';
import { bs } from '../../../../styles/styles';
import { Checkbox } from '../../../Checkbox';
import { DefaultSelectBox, ISelectBoxOption } from '../../../SelectBox';
import { TextInput } from '../../../TextInput';
import { styleSheet } from './../styles';

export function OpportunityStepConfig(props: {
	canEdit?: boolean;
	templateId: string;
	step: Api.AutomationTemplateEditorStep<Api.AutomationStepViewModel<Api.UserSessionContext, Api.IAutomationStep>>;
	clearInlineErrorMessage: () => void;
	onStepChanged?: () => void;
}) {
	const boardsQuery = useInfiniteBoardsQuery({
		type: 'Opportunity',
		meta: {
			toastErrorMessage: 'Error loading boards',
		},
	});
	const step = props.step.automationStep.toJs() as Api.IOpportunityAutomationStep;
	const [selectedBoard, setSelectedBoard] = React.useState<Api.IBoard>(step.boardReference);
	const [dealSize, setDealSize] = React.useState(numberToCurrencyStringValue(step.dealSize ?? 0));
	const [updateExisting, setUpdateExisting] = React.useState(step.updateExistingOpportunity);

	const boardOptions = boardsQuery.data?.pages
		.map(page => page.values)
		.flat()
		?.map(x => {
			return {
				title: <span className={css(styleSheet.opportunityDropdownOption)}>{x.name}</span>,
				value: x,
			};
		});

	const [stages, setStages] = React.useState<ISelectBoxOption<Api.IBoardStage>[]>([]);
	const [selectedStage, setSelectedStage] = React.useState<Api.IBoardStage>(step.boardStageReference);

	React.useEffect(() => {
		const board = boardsQuery.data?.pages
			.map(page => page.values)
			.flat()
			.find(x => x.id === step.boardReference?.id);
		setSelectedBoard(board);
		setStages(
			board?.stages?.map(x => {
				return {
					title: <span className={css(styleSheet.opportunityDropdownOption)}>{x.name}</span>,
					value: x,
				};
			})
		);
	}, [boardsQuery.data, step.boardReference]);

	const onBoardSelectionChanged = (board: ISelectBoxOption<Api.IBoard>) => {
		if (selectedBoard && board?.value.id === selectedBoard.id) {
			return;
		}

		setSelectedBoard(board.value);
		setSelectedStage(board.value.stages[0]);
		setStages(
			board.value.stages.map(x => {
				return {
					title: <span className={css(styleSheet.opportunityDropdownOption)}>{x.name}</span>,
					value: x,
				};
			})
		);

		save({ board: board.value, stage: board.value.stages[0] });
	};
	const onStageSelectionChanged = (stage: ISelectBoxOption<Api.IBoardStage>) => {
		if (selectedStage && stage?.value.id === selectedStage.id) {
			return;
		}
		setSelectedStage(stage.value);
		save({ stage: stage.value });
	};
	const save = ({
		board = selectedBoard,
		stage = selectedStage,
		updateExistingOpportunity = updateExisting,
	}: {
		board?: Api.IBoard;
		stage?: Api.IBoardStage;
		updateExistingOpportunity?: boolean;
	}) => {
		props.step.automationStep.update({
			...step,
			boardReference: board,
			boardStageReference: stage,
			dealSize: updateExistingOpportunity ? null : parseFloat(sanitizeCurrencyStringValue(dealSize)),
			updateExistingOpportunity,
		} as Api.IOpportunityAutomationStep);
		props.onStepChanged?.();
	};
	const onDealSizeChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setDealSize(e.target.value);
		props.clearInlineErrorMessage();
	};
	const onUpdateExistingChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setUpdateExisting(e.target.checked);
		if (e.target.checked) {
			setDealSize('');
		}
		save({ updateExistingOpportunity: e.target.checked });
	};

	return (
		<>
			<Checkbox
				id={`automation-opportunity-step-updateExisting-checkbox-${props.step?.uuid || ''}`}
				checked={!!updateExisting}
				type='large'
				onChange={onUpdateExistingChanged}
				className={css(styleSheet.updateExistingLabel)}
			>
				<div>
					<p className={css(styleSheet.noMargin, styleSheet.checkboxText)}>
						Update an existing opportunity instead of creating a new one.
					</p>
				</div>
			</Checkbox>
			<div className={css(styleSheet.fieldLabel)}>
				Select an opportunity board: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<DefaultSelectBox
				className={css(styleSheet.typeSelect)}
				disabled={!props.canEdit}
				onSelectionChanged={onBoardSelectionChanged}
				options={boardOptions}
				triggerClassName={css(styleSheet.opportunityDropdown)}
				optionStyles={[styleSheet.typeSelectMenuItem]}
				placeholder={<span className={css(bs.textTitles, bs.textSm)}>Select a choice below...</span>}
				contentClassName={css(styleSheet.typeSelectContent)}
				selectedOption={boardOptions?.find(x => x.value.id === selectedBoard?.id)}
			/>
			<div className={css(styleSheet.fieldLabel, styleSheet.stageLabel)}>
				Select a stage: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<DefaultSelectBox
				className={css(styleSheet.typeSelect)}
				disabled={!props.canEdit || !selectedBoard}
				onSelectionChanged={onStageSelectionChanged}
				options={stages}
				triggerClassName={css(styleSheet.opportunityDropdown)}
				optionStyles={[styleSheet.typeSelectMenuItem]}
				placeholder={<span className={css(bs.textTitles, bs.textSm)}>Select a choice below...</span>}
				contentClassName={css(styleSheet.typeSelectContent)}
				selectedOption={stages?.find(x => x.value.id === selectedStage?.id) ?? stages?.[0]}
			/>

			{!updateExisting && (
				<>
					<div className={css(styleSheet.fieldLabel, styleSheet.stageLabel)}>Amount: &nbsp;</div>
					<TextInput
						className={css(styleSheet.emailNameInput)}
						disabled={!props.canEdit}
						inputId={`automation-opportunity-step-amount-input-${props.step?.uuid || ''}`}
						key={`${props.step?.uuid}-name-input`}
						leftAccessory={<span className={css(styleSheet.dollar)}>$</span>}
						onBlur={() => save({})}
						onChange={onDealSizeChanged}
						onFocus={props.clearInlineErrorMessage}
						placeholder='0.00'
						type='text'
						value={dealSize || ''}
					/>
				</>
			)}
		</>
	);
}
